/**
 * How the garage renders a stat.
 *
 * The values themselves are always the server's — this file decides only what
 * to call them, how to format them, and which direction is an improvement.
 * `reloadSeconds` is the reason the last part exists: a smaller number is a
 * better gun, and a comparison arrow that pointed the other way would quietly
 * mislead everyone reading it.
 */
export interface StatDescriptor {
  key: string;
  label: string;
  unit: string;
  /** Value at which the bar is full. Chosen above anything in the catalogue. */
  full: number;
  /** True when a lower number is the better one. */
  lowerIsBetter?: boolean;
  decimals?: number;
  /** Which heading the readout files this under. See `StatGroup`. */
  group: StatGroup;
  /** Multiplies the raw value for display only — the bar fill and deltas read the raw value. */
  displayScale?: number;
}

/**
 * How the readout groups stats for a reader.
 *
 * **A second, independent axis — not a rename of the hull/turret split.** The
 * two genuinely cut across each other: turret traverse is a *turret* stat and
 * belongs under Firepower, hull integrity is a *hull* stat and belongs under
 * Survivability. Both axes are kept because both are used: the customise window
 * compares a part against the half of the tank it bolts to, and the readout
 * answers "what is this tank good at".
 *
 * There is deliberately **no headline score per group**. A single number has to
 * be invented, and an invented number becomes a real balance constraint the
 * moment players start optimising for it.
 */
export type StatGroup = 'firepower' | 'survivability' | 'mobility';

/** Group headings, in the order the readout shows them. */
export const STAT_GROUPS: ReadonlyArray<{ id: StatGroup; label: string }> = [
  { id: 'firepower', label: 'Firepower' },
  { id: 'survivability', label: 'Survivability' },
  { id: 'mobility', label: 'Mobility' },
];

// The ceilings below are measured, not guessed: every catalogue item resolved at
// its own `maxUpgradeLevel` and then through `applyPrestige` at
// `PRESTIGE_MAX_LEVEL`, with a little headroom on top. Prestige is the part that
// is easy to forget — it lifts stats *past* the catalogue's upgrade cap, so
// ceilings set from the upgrade curve alone leave the best items in the game
// sitting at a permanently full bar, unable to show what they cost.

// Speed, acceleration and reverse are converted to km/h / km/h/s for display —
// `full` stays in the server's own m/s so the bar fill reads the raw value.
export const HULL_STATS: StatDescriptor[] = [
  { key: 'maxHealth', label: 'Hull integrity', unit: 'hp', full: 380, group: 'survivability' },
  // Armour is a hard cap in the simulation, not a catalogue maximum, so the bar
  // is scaled to the cap: a full bar means immune-as-the-game-allows.
  { key: 'armour', label: 'Armour', unit: '', full: 0.8, decimals: 2, group: 'survivability' },
  { key: 'maxForwardSpeed', label: 'Top speed', unit: 'km/h', full: 31, decimals: 1, group: 'mobility', displayScale: 3.6 },
  { key: 'acceleration', label: 'Acceleration', unit: 'km/h/s', full: 4.3, decimals: 1, group: 'mobility', displayScale: 3.6 },
  { key: 'hullTurnRate', label: 'Hull traverse', unit: 'rad/s', full: 4, decimals: 2, group: 'mobility' },
  { key: 'maxReverseSpeed', label: 'Reverse', unit: 'km/h', full: 15, decimals: 1, group: 'mobility', displayScale: 3.6 },
];

export const TURRET_STATS: StatDescriptor[] = [
  { key: 'damage', label: 'Shell damage', unit: '', full: 150, group: 'firepower' },
  // Sustained DPS counts every shell in a volley, so a scattergun's eight
  // pellets and a quad barrel's four land far above any single-shot gun.
  { key: 'damagePerSecond', label: 'Sustained DPS', unit: '/s', full: 80, decimals: 1, group: 'firepower' },
  // Scaled to the rail's unupgraded reload, the longest in the game.
  { key: 'reloadSeconds', label: 'Reload', unit: 's', full: 5, lowerIsBetter: true, decimals: 2, group: 'firepower' },
  { key: 'range', label: 'Range', unit: 'm', full: 400, group: 'firepower' },
  { key: 'muzzleVelocity', label: 'Muzzle velocity', unit: 'm/s', full: 640, group: 'firepower' },
  // A turret stat that is plainly firepower: how fast the gun comes onto a
  // target decides whether it fires at all. The clearest case of why `group`
  // could not just be the hull/turret split renamed.
  { key: 'turretSlewRate', label: 'Turret traverse', unit: 'rad/s', full: 6.5, decimals: 2, group: 'firepower' },
];

/** `SPEC_STATS` split by group, in `STAT_GROUPS` order. Empty groups are dropped. */
export function groupedSpecStats(): Array<{ id: StatGroup; label: string; stats: StatDescriptor[] }> {
  return STAT_GROUPS.map((g) => ({
    ...g,
    stats: SPEC_STATS.filter((s) => s.group === g.id),
  })).filter((g) => g.stats.length > 0);
}

/** The resolved spec, laid out the way the loadout panel shows it. */
export const SPEC_STATS: StatDescriptor[] = [...HULL_STATS, ...TURRET_STATS].filter(
  (s) => s.key !== 'damagePerSecond',
);

/**
 * A resolved spec, narrowed to the numbers `SPEC_STATS` describes.
 *
 * Here rather than in a component because two screens now show the same
 * readout — the shop sidebar and the garage lobby — and a second copy of this
 * mapping is a second place for a stat to go missing when one is added.
 * `TankSpec` also carries model paths and item ids, which are not numbers and
 * have no business in a stat list.
 */
export function specValues(spec: Record<string, unknown> | null): Record<string, number> {
  if (!spec) return {};
  const out: Record<string, number> = {};
  for (const descriptor of SPEC_STATS) {
    const value = spec[descriptor.key];
    if (typeof value === 'number' && Number.isFinite(value)) out[descriptor.key] = value;
  }
  return out;
}

export function formatStat(descriptor: StatDescriptor, value: number): string {
  const decimals = descriptor.decimals ?? 0;
  const shown = (value * (descriptor.displayScale ?? 1)).toFixed(decimals);
  return descriptor.unit ? `${shown} ${descriptor.unit}` : shown;
}

/** 0–1, for the bar width. */
export function fillFraction(descriptor: StatDescriptor, value: number): number {
  const raw = descriptor.lowerIsBetter
    ? 1 - value / descriptor.full
    : value / descriptor.full;
  return Math.max(0.02, Math.min(1, raw));
}

/** -1 worse, 0 same, 1 better — from the player's point of view. */
export function compare(descriptor: StatDescriptor, candidate: number, current: number): number {
  const epsilon = 1e-6;
  const difference = candidate - current;
  if (Math.abs(difference) < epsilon) return 0;
  const better = descriptor.lowerIsBetter ? difference < 0 : difference > 0;
  return better ? 1 : -1;
}

export function formatCredits(value: number): string {
  return value.toLocaleString('en-GB');
}

/**
 * A balance, shortened for the title bar.
 *
 * `formatCredits` is still what every *price* uses, and must stay that way: a
 * figure somebody is about to pay is stated exactly, so an item at 3,600 credits
 * never reads as `3.6K` on the card or in the confirmation. This is the other
 * question — *roughly how much have I got* — asked of a readout that shares one
 * crowded row with the fuel strip, a rank chip, a title, a call sign and two
 * buttons, and whose width changes every time money moves. Six digits and a
 * separator there is what pushes the sign-out button off the end.
 *
 * **It truncates, never rounds**, which is the one rule in here that is not
 * taste: a balance of 9,999 shown as `10K` tells a player they can afford
 * something they cannot, and being told by your own wallet that you have money
 * you do not is the worst way for a shop to be wrong. Rounding *down* can only
 * ever understate, and understating a balance costs nobody a purchase they were
 * entitled to — the shop's own affordability checks read the real number.
 *
 * One decimal below ten units and none above (`9.9K`, `12K`, `345K`, `1.2M`),
 * which is the shortest form that still moves when a sortie's pay lands.
 *
 * The exact figure is never lost: both chips publish it as `data-value` and say
 * it in full in their tooltip, which is also what the e2e harness reads — the
 * text is the shortening, the DOM keeps the truth.
 */
export function formatBalance(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const sign = value < 0 ? '-' : '';
  const magnitude = Math.abs(value);
  if (magnitude < 1_000) return `${sign}${Math.trunc(magnitude)}`;

  const [divisor, suffix] = magnitude < 1_000_000 ? [1_000, 'K'] : [1_000_000, 'M'];
  const scaled = magnitude / divisor;
  // Truncated at the digit it is about to show, so neither form can round up.
  const shown = scaled < 10 ? `${Math.floor(scaled * 10) / 10}` : `${Math.floor(scaled)}`;
  return `${sign}${shown}${suffix}`;
}
